import {
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid2,
  OutlinedInput,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { userStore } from "../../../../../stores/sharedStores";
import { SwayyEmployeeRoles } from "../../../../../models/Employees/SwayyEmployeeRoles";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { SwayyPriceListType } from "../../../models/pricing/SwayyPriceList";
import { SwayyPriceListsStore } from "../../../stores/SwayyPriceListsStore";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";
import { SwayyPageHeader, SwayyTableEmptyState } from "../../components/SwayyPageShell";

const TYPE_OPTIONS: Array<{ id: SwayyPriceListType; value: string }> = [
  { id: "b2c", value: "B2C (retail)" },
  { id: "b2b", value: "B2B (wholesale)" },
];

const primaryBtnSx = {
  minHeight: 30,
  px: 1.5,
  py: 0,
  fontSize: 12,
  color: swayyColors.white,
  backgroundColor: swayyColors.pine,
  "&:hover": {
    backgroundColor: swayyColors.pineHover,
    color: swayyColors.white,
  },
  "&.Mui-disabled": {
    color: swayyColors.white,
    opacity: 0.55,
  },
} as const;

const secondaryBtnSx = {
  minHeight: 30,
  px: 1.5,
  py: 0,
  fontSize: 12,
} as const;

const inputSx = {
  borderRadius: swayyRadii.md,
  fontFamily: swayyFont,
  fontSize: 13,
  height: 32,
  bgcolor: swayyColors.white,
  "& .MuiOutlinedInput-input": {
    py: 0.5,
    height: 32,
    boxSizing: "border-box",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: swayyColors.border },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
} as const;

const SwayyPriceLists = () => {
  const { showError, showSuccess } = useNotification();
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;
  const storeRef = useRef(new SwayyPriceListsStore());
  const store = storeRef.current;
  const debouncedSearch = useDebounce(store.searchDraft, 400);
  const [createOpen, setCreateOpen] = useState(false);
  const canViewCatalog = userStore.hasSwayyPermission([
    SwayyEmployeeRoles.VIEW_CATALOG,
    SwayyEmployeeRoles.EDIT_CATALOG,
  ]);
  const canEditCatalog = userStore.hasSwayyPermission(
    SwayyEmployeeRoles.EDIT_CATALOG,
  );

  useEffect(() => {
    if (!canViewCatalog) return;
    void store.loadLists().then((error) => {
      if (error) {
        showErrorRef.current("Error", "Failed to load price lists.");
      }
    });
    return () => store.dispose();
  }, [canViewCatalog, store]);

  useEffect(() => {
    if (!canViewCatalog) return;
    store.setSearch(debouncedSearch);
  }, [canViewCatalog, debouncedSearch, store]);

  const openCreate = (asDefaultB2c = false) => {
    if (asDefaultB2c) {
      store.prepareDefaultB2cCreate();
    } else {
      store.resetCreateForm({
        isDefault: !store.hasDefaultB2c,
      });
    }
    setCreateOpen(true);
  };

  const handleCloseCreate = () => {
    setCreateOpen(false);
    store.resetCreateForm();
  };

  const handleCreate = async () => {
    if (!store.canCreate) {
      showError("Validation", "Name and a 3-letter currency are required.");
      return;
    }
    const result = await store.createList();
    if (result.error) {
      showError("Error", "Failed to create price list.");
      return;
    }
    setCreateOpen(false);
    showSuccess("Price list created.");
  };

  const handleSaveEdit = async () => {
    if (!store.canSaveEdit) {
      showError("Validation", "Name and a 3-letter currency are required.");
      return;
    }
    const error = await store.saveEdit();
    if (error) {
      showError("Error", "Failed to update price list.");
      return;
    }
    showSuccess("Price list updated.");
  };

  if (!canViewCatalog) {
    return <PermissionBlockLabel />;
  }

  return (
    <Grid2 container sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      <Grid2 size={12}>
      <SwayyPageHeader
        title="Price lists"
        description="Create the default B2C list once, then set MRP and sale price on each product’s Variants tab."
        actions={
          canEditCatalog ? (
            <Stack direction="row" spacing={1}>
              {!store.hasDefaultB2c && store.listState === "ready" ? (
                <AppButton
                  label="Create Default B2C"
                  variant="primary"
                  onClick={() => openCreate(true)}
                  customStyles={primaryBtnSx}
                />
              ) : null}
              <AppButton
                label="Add price list"
                variant="primary"
                onClick={() => openCreate(false)}
                customStyles={primaryBtnSx}
              />
            </Stack>
          ) : undefined
        }
      />

      {!store.hasDefaultB2c && store.listState === "ready" && canEditCatalog ? (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            borderRadius: swayyRadii.lg,
            borderColor: swayyColors.border,
            backgroundColor: swayyColors.pageBg,
          }}
        >
          <CustomTypography
            variant="body2"
            color={swayyColors.textSecondary}
            sx={{ mb: 1.5 }}
          >
            No default B2C price list yet. Create one so product Variants can
            save retail prices and products can go Live.
          </CustomTypography>
          <AppButton
            label="Create Default B2C"
            variant="primary"
            onClick={() => openCreate(true)}
            customStyles={primaryBtnSx}
          />
        </Paper>
      ) : null}

      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: swayyRadii.lg,
          borderColor: swayyColors.border,
        }}
      >
        <TextField
          size="small"
          placeholder="Search price lists"
          value={store.searchDraft}
          onChange={(event) => store.setSearchDraft(event.target.value)}
          fullWidth
          sx={{
            maxWidth: 360,
            fontFamily: swayyFont,
            "& .MuiInputBase-root": { height: 32, fontSize: 13 },
          }}
        />
      </Paper>

      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          borderRadius: swayyRadii.lg,
          borderColor: swayyColors.border,
          overflow: "hidden",
        }}
      >
        {store.listState === "loading" || store.listState === "idle" ? (
          <Box sx={{ p: 2 }}>
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
          </Box>
        ) : store.listState === "error" ? (
          <SwayyTableEmptyState
            title="Couldn't load price lists"
            description={store.errorMessage || "Network Error"}
            actionLabel="Try again"
            onAction={() => {
              void store.loadLists().then((error) => {
                if (error) {
                  showError("Error", "Failed to load price lists.");
                }
              });
            }}
            danger
          />
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell>Default</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {store.filteredLists.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <CustomTypography
                      variant="body2"
                      color={swayyColors.textSecondary}
                    >
                      No price lists yet. Create Default B2C to get started.
                    </CustomTypography>
                  </TableCell>
                </TableRow>
              ) : (
                store.filteredLists.map((list) => (
                  <TableRow
                    key={list.id}
                    hover={canEditCatalog}
                    sx={{ cursor: canEditCatalog ? "pointer" : "default" }}
                    onClick={() => {
                      if (canEditCatalog) store.startEdit(list);
                    }}
                  >
                    <TableCell>
                      <CustomTypography
                        variant="body2"
                        color={swayyColors.deepGreen}
                        fontWeight={700}
                      >
                        {list.name}
                      </CustomTypography>
                    </TableCell>
                    <TableCell>
                      {list.type === "b2c" ? "B2C" : "B2B"}
                    </TableCell>
                    <TableCell>{list.currency}</TableCell>
                    <TableCell>
                      {list.isDefault ? (
                        <Chip
                          size="small"
                          label="Default"
                          sx={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 700,
                            bgcolor: swayyColors.pageBg,
                          }}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {list.isActive ? "Active" : "Inactive"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={createOpen}
        onClose={handleCloseCreate}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add price list</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <OutlinedInput
              value={store.createForm.name}
              onChange={(event) =>
                store.updateCreateForm("name", event.target.value)
              }
              placeholder="Name (e.g. Default B2C)"
              fullWidth
              sx={inputSx}
            />
            <AppSearchDropdownMulti
              isMultiSelect={false}
              title="Type"
              list={TYPE_OPTIONS.map((item) => ({
                id: item.id,
                value: item.value,
              }))}
              value={[
                {
                  id: store.createForm.type,
                  value:
                    TYPE_OPTIONS.find(
                      (item) => item.id === store.createForm.type,
                    )?.value || store.createForm.type,
                },
              ]}
              local
              accentColor={swayyColors.pine}
              borderColor={swayyColors.border}
              handleSelect={(selected) => {
                const next = selected[0]?.id as SwayyPriceListType | undefined;
                if (next) store.updateCreateForm("type", next);
              }}
            />
            <OutlinedInput
              value={store.createForm.currency}
              onChange={(event) =>
                store.updateCreateForm(
                  "currency",
                  event.target.value.toUpperCase().slice(0, 3),
                )
              }
              placeholder="Currency (INR)"
              fullWidth
              sx={inputSx}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={store.createForm.isDefault}
                  onChange={(event) =>
                    store.updateCreateForm("isDefault", event.target.checked)
                  }
                  size="small"
                />
              }
              label="Mark as default for this type"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <AppButton
            label="Cancel"
            variant="secondary"
            onClick={handleCloseCreate}
            customStyles={secondaryBtnSx}
          />
          <AppButton
            label={store.isSaving ? "Creating..." : "Create"}
            variant="primary"
            disabled={store.isSaving || !store.canCreate}
            onClick={() => void handleCreate()}
            customStyles={primaryBtnSx}
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={store.editingId != null && store.editForm != null}
        onClose={() => store.clearEdit()}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit price list</DialogTitle>
        <DialogContent>
          {store.editForm ? (
            <Stack spacing={1.5} sx={{ pt: 1 }}>
              <OutlinedInput
                value={store.editForm.name}
                onChange={(event) =>
                  store.updateEditForm("name", event.target.value)
                }
                placeholder="Name"
                fullWidth
                sx={inputSx}
              />
              <OutlinedInput
                value={store.editForm.currency}
                onChange={(event) =>
                  store.updateEditForm(
                    "currency",
                    event.target.value.toUpperCase().slice(0, 3),
                  )
                }
                placeholder="Currency"
                fullWidth
                sx={inputSx}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={store.editForm.isDefault}
                    onChange={(event) =>
                      store.updateEditForm("isDefault", event.target.checked)
                    }
                    size="small"
                  />
                }
                label="Mark as default for this type"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={store.editForm.isActive}
                    onChange={(event) =>
                      store.updateEditForm("isActive", event.target.checked)
                    }
                    size="small"
                  />
                }
                label="Active"
              />
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <AppButton
            label="Cancel"
            variant="secondary"
            onClick={() => store.clearEdit()}
            customStyles={secondaryBtnSx}
          />
          <AppButton
            label={store.isSaving ? "Saving..." : "Save"}
            variant="primary"
            disabled={store.isSaving || !store.canSaveEdit}
            onClick={() => void handleSaveEdit()}
            customStyles={primaryBtnSx}
          />
        </DialogActions>
      </Dialog>
      </Grid2>
    </Grid2>
  );
};

export default observer(SwayyPriceLists);
